import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { Container } from "./Container";
import { labelForPath } from "@/lib/internal-links";
import { canonical } from "@/lib/seo";

export type Crumb = { href: string; label?: string };

export interface BreadcrumbsProps {
  /** Ordered list of breadcrumb segments, excluding Home (added automatically). */
  items: Crumb[];
  /** Background style. "dark" places it over hero/dark imagery; "light" sits on the page surface. */
  variant?: "light" | "dark";
}

/**
 * Visible breadcrumb nav + matching BreadcrumbList JSON-LD for SEO.
 */
export function Breadcrumbs({ items, variant = "light" }: BreadcrumbsProps) {
  const full: Crumb[] = [{ href: "/", label: "Home" }, ...items];
  const last = full[full.length - 1];

  const baseLink =
    variant === "dark"
      ? "text-white/75 hover:text-white"
      : "text-[var(--muted-foreground)] hover:text-primary";
  const currentClass = variant === "dark" ? "text-white font-medium" : "text-foreground font-medium";
  const sepClass = variant === "dark" ? "text-white/40" : "text-[var(--muted-foreground)]/60";

  const ld = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: full.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.label ?? labelForPath(c.href),
      item: canonical(c.href),
    })),
  };

  return (
    <nav aria-label="Breadcrumb" className={variant === "dark" ? "py-3" : "border-b border-border bg-white"}>
      <Container className={variant === "dark" ? "" : "py-3"}>
        <ol className="flex flex-wrap items-center gap-1.5 text-xs">
          {full.map((c, i) => {
            const isLast = c === last;
            const label = c.label ?? labelForPath(c.href);
            return (
              <li key={c.href + i} className="flex items-center gap-1.5">
                {i > 0 && <ChevronRight className={`h-3 w-3 ${sepClass}`} aria-hidden="true" />}
                {isLast ? (
                  <span className={currentClass} aria-current="page">
                    {label}
                  </span>
                ) : (
                  <Link to={c.href} className={baseLink}>
                    {label}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </Container>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />
    </nav>
  );
}
